/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import { Selector, Selector$inboundSchema } from "./selector.js";

/**
 * The scope slug this grant applies to.
 */
export const ListRoleGrantScope = {
  OrgRead: "org:read",
  OrgBlockedRead: "org:blocked_read",
  OrgAdmin: "org:admin",
  OrgBlockedAdmin: "org:blocked_admin",
  ProjectRead: "project:read",
  ProjectBlockedRead: "project:blocked_read",
  ProjectWrite: "project:write",
  ProjectBlockedWrite: "project:blocked_write",
  McpRead: "mcp:read",
  McpBlockedRead: "mcp:blocked_read",
  McpWrite: "mcp:write",
  McpBlockedWrite: "mcp:blocked_write",
  McpConnect: "mcp:connect",
  McpBlockedConnect: "mcp:blocked_connect",
  EnvironmentRead: "environment:read",
  EnvironmentBlockedRead: "environment:blocked_read",
  EnvironmentWrite: "environment:write",
  EnvironmentBlockedWrite: "environment:blocked_write",
  SkillRead: "skill:read",
  SkillBlockedRead: "skill:blocked_read",
  SkillWrite: "skill:write",
  SkillBlockedWrite: "skill:blocked_write",
  AssistantRead: "assistant:read",
  AssistantBlockedRead: "assistant:blocked_read",
  AssistantWrite: "assistant:write",
  AssistantBlockedWrite: "assistant:blocked_write",
  PluginWrite: "plugin:write",
  PluginBlockedWrite: "plugin:blocked_write",
  RiskPolicyEvaluate: "risk_policy:evaluate",
  RiskPolicyBypass: "risk_policy:bypass",
  RiskPolicyBlock: "risk_policy:block",
  ChatRead: "chat:read",
  ChatWrite: "chat:write",
  LogRead: "log:read",
  AgentRead: "agent:read",
  AgentWrite: "agent:write",
  AgentAuthorize: "agent:authorize",
  AgentTransfer: "agent:transfer",
  WorkloadRead: "workload:read",
  WorkloadBlockedRead: "workload:blocked_read",
  WorkloadWrite: "workload:write",
  WorkloadBlockedWrite: "workload:blocked_write",
  OrgDeviceAgentSync: "org:device_agent_sync",
  OrgHooksIngest: "org:hooks_ingest",
} as const;
/**
 * The scope slug this grant applies to.
 */
export type ListRoleGrantScope = OpenEnum<typeof ListRoleGrantScope>;

export const SubScopes = {
  OrgRead: "org:read",
  OrgBlockedRead: "org:blocked_read",
  OrgAdmin: "org:admin",
  OrgBlockedAdmin: "org:blocked_admin",
  ProjectRead: "project:read",
  ProjectBlockedRead: "project:blocked_read",
  ProjectWrite: "project:write",
  ProjectBlockedWrite: "project:blocked_write",
  McpRead: "mcp:read",
  McpBlockedRead: "mcp:blocked_read",
  McpWrite: "mcp:write",
  McpBlockedWrite: "mcp:blocked_write",
  McpConnect: "mcp:connect",
  McpBlockedConnect: "mcp:blocked_connect",
  EnvironmentRead: "environment:read",
  EnvironmentBlockedRead: "environment:blocked_read",
  EnvironmentWrite: "environment:write",
  EnvironmentBlockedWrite: "environment:blocked_write",
  SkillRead: "skill:read",
  SkillBlockedRead: "skill:blocked_read",
  SkillWrite: "skill:write",
  SkillBlockedWrite: "skill:blocked_write",
  AssistantRead: "assistant:read",
  AssistantBlockedRead: "assistant:blocked_read",
  AssistantWrite: "assistant:write",
  AssistantBlockedWrite: "assistant:blocked_write",
  PluginWrite: "plugin:write",
  PluginBlockedWrite: "plugin:blocked_write",
  RiskPolicyEvaluate: "risk_policy:evaluate",
  RiskPolicyBypass: "risk_policy:bypass",
  RiskPolicyBlock: "risk_policy:block",
  ChatRead: "chat:read",
  ChatWrite: "chat:write",
  LogRead: "log:read",
  AgentRead: "agent:read",
  AgentWrite: "agent:write",
  AgentAuthorize: "agent:authorize",
  AgentTransfer: "agent:transfer",
  WorkloadRead: "workload:read",
  WorkloadBlockedRead: "workload:blocked_read",
  WorkloadWrite: "workload:write",
  WorkloadBlockedWrite: "workload:blocked_write",
  OrgDeviceAgentSync: "org:device_agent_sync",
  OrgHooksIngest: "org:hooks_ingest",
} as const;
export type SubScopes = OpenEnum<typeof SubScopes>;

export type ListRoleGrant = {
  /**
   * The subset of this scope's selectors granted to the calling user by name rather than through a role or everyone. For allow scopes it holds only selectors naming a concrete resource, which outrank blocks inherited from roles or everyone on that resource. For blocked scopes it holds the caller's own blocks, which always apply. Omitted when empty.
   */
  directSelectors?: Array<Selector> | undefined;
  /**
   * The scope slug this grant applies to.
   */
  scope: ListRoleGrantScope;
  /**
   * Selector constraints. Null means unrestricted.
   */
  selectors?: Array<Selector> | undefined;
  /**
   * The inherited scopes the primary scope grants.
   */
  subScopes?: Array<SubScopes> | undefined;
};

/** @internal */
export const ListRoleGrantScope$inboundSchema: z.ZodMiniType<
  ListRoleGrantScope,
  unknown
> = openEnums.inboundSchema(ListRoleGrantScope);

/** @internal */
export const SubScopes$inboundSchema: z.ZodMiniType<SubScopes, unknown> =
  openEnums.inboundSchema(SubScopes);

/** @internal */
export const ListRoleGrant$inboundSchema: z.ZodMiniType<
  ListRoleGrant,
  unknown
> = z.pipe(
  z.object({
    direct_selectors: z.optional(z.array(Selector$inboundSchema)),
    scope: ListRoleGrantScope$inboundSchema,
    selectors: z.optional(z.array(Selector$inboundSchema)),
    sub_scopes: z.optional(z.array(SubScopes$inboundSchema)),
  }),
  z.transform((v) => {
    return remap$(v, {
      "direct_selectors": "directSelectors",
      "sub_scopes": "subScopes",
    });
  }),
);

export function listRoleGrantFromJSON(
  jsonString: string,
): SafeParseResult<ListRoleGrant, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListRoleGrant$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListRoleGrant' from JSON`,
  );
}
